import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Container, IconButton, Tooltip, Typography } from '@mui/material';
import { PictureAsPdf } from '@material-ui/icons';
import { dispatch, useSelector } from '../../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../../routes/paths';
import useSettings from '../../../../../hooks/useSettings';
import Page from '../../../../../components/Page';
import HeaderBreadcrumbs from '../../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../../components/table/DataGridTable';
import PdfViewerDrawer from '../../../../../components/pdfViewerDrawer';
import PermissionRequired from '../../../../errorPages/permissionRequired';
import { getUtilityBillsToBePaid } from '../../../../../redux/slices/financeRedux/utilityBillPaymentRedux';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../../config';
import { accessVerify, capitalize, handleNumber, safeJSONParse, utcMoment } from '../../../../../utils/common';
import { fCurrency } from '../../../../../utils/formatNumber';

const BILL_TYPES = [
  { label: 'Regular Bill', value: 'REGULAR' },
  { label: 'Service Bill', value: 'SERVICE' },
];

const getDateForDisplay = (value) => (value ? utcMoment(value).format('YYYY-MM-DD') : '-');

const normalizeDocuments = (linkedDocuments) => {
  const parsedDocuments = safeJSONParse(linkedDocuments);
  if (!Array.isArray(parsedDocuments)) return [];
  return parsedDocuments
    .map((value) => {
      if (typeof value === 'string') return value;
      return value?.url;
    })
    .filter((value) => value);
};

const parseWorkflowLogs = (bill) => {
  const parsedLog = safeJSONParse(bill?.workflowLog);
  if (!Array.isArray(parsedLog)) return [];

  return parsedLog.map((log) => ({
    ...log,
    date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
  }));
};

const getUtilityBillPdfDescription = (bill) => {
  const period =
    bill?.billingPeriodStart || bill?.billingPeriodEnd ? `Period: ${getDateForDisplay(bill?.billingPeriodStart)} to ${getDateForDisplay(bill?.billingPeriodEnd)}` : null;
  const readings = bill?.billType === 'REGULAR' && bill?.accountBillingType === 'METERED' ? `Readings: ${bill?.previousReading ?? '-'} to ${bill?.currentReading ?? '-'}` : null;
  const service = bill?.billType === 'SERVICE' ? `Service: ${bill?.serviceDescription || '-'}` : null;

  return [BILL_TYPES.find((value) => value.value === bill?.billType)?.label || bill?.billType, period, readings, service].filter(Boolean).join(' | ');
};

export default function UtilityBillsToBePaid() {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const { utilityBillsToBePaid, addData } = useSelector((state) => state.utilityBillPayment);

  const [rowSelection, setRowSelection] = useState(false);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [workflowLog, setWorkflowLog] = useState([]);

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getUtilityBillsToBePaid(selectedLocation.id));
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    if (addData?.time && selectedLocation?.id) {
      dispatch(getUtilityBillsToBePaid(selectedLocation.id));
    }
  }, [addData, selectedLocation?.id]);

  const totalAmountToBePaid = useMemo(() => utilityBillsToBePaid.reduce((total, value) => total + handleNumber(value?.outstandingAmount), 0), [utilityBillsToBePaid]);

  const handleDownload = (row) => {
    const bill = row?.original || {};
    const totalAmount = handleNumber(bill?.totalAmount);
    const paidAmount = handleNumber(bill?.paidAmount);

    setPdfRow({
      invoiceNumber: `UB-${bill?.id}`,
      createDate: bill?.billDate ? utcMoment(bill.billDate).format('DD MMMM yyyy').toString() : '-',
      dueDate: bill?.dueDate ? utcMoment(bill.dueDate).format('DD MMMM yyyy').toString() : '-',
      status: bill?.status || '-',
      discount: 0,
      taxes: 0,
      paidAmount,
      subTotalPrice: totalAmount,
      totalPrice: totalAmount - paidAmount,
      topic: 'Utility Bill',
      referenceNumber: bill?.referenceNumber,
      invoiceFrom: {
        name: bill?.utilitySupplierName || '-',
        address: bill?.utilitySupplierAddress || '',
        phone: bill?.utilitySupplierContactNumber || '',
      },
      invoiceTo: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: bill?.locationAddress || '',
        phone: bill?.locationContactNumber || '',
      },
      items: [
        {
          id: 1,
          title: `${bill?.utilityCategoryName || 'Utility'} - ${bill?.billingAccountName || bill?.utilityAccountName || '-'}`,
          description: getUtilityBillPdfDescription(bill),
          quantity: 1,
          measurementLabel: 'Billing Type',
          measurementValue: bill?.accountBillingType === 'METERED' ? 'Metered' : bill?.accountBillingType === 'NON_METERED' ? 'Non Metered' : '-',
          price: totalAmount,
        },
      ],
      stockCodes: [],
      linkedDocuments: normalizeDocuments(bill?.linkedDocuments).map((url) => url?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)),
    });
    setWorkflowLog(parseWorkflowLogs(bill));
    setIsPdfDrawerOpen(true);
  };

  const columns = [
    { accessorKey: 'id', header: 'Utility Bill' },
    { accessorKey: 'locationName', header: 'Location' },
    {
      accessorKey: 'billingAccountName',
      header: 'Account / Meter',
      Cell: ({ row }) => `${row?.original?.billingAccountName || row?.original?.utilityAccountName || '-'}${row?.original?.meterNumber ? ` (${row?.original?.meterNumber})` : ''}`,
    },
    {
      accessorKey: 'utilitySupplierName',
      header: 'Supplier',
      Cell: ({ row }) => (row?.original?.utilitySupplierName ? `${row?.original?.utilitySupplierName}${row?.original?.utilitySupplierCode ? ` (${row?.original?.utilitySupplierCode})` : ''}` : '-'),
    },
    { accessorKey: 'utilityCategoryName', header: 'Category' },
    { accessorKey: 'referenceNumber', header: 'Reference Number' },
    {
      accessorKey: 'billType',
      header: 'Bill Type',
      Cell: ({ row }) => BILL_TYPES.find((value) => value.value === row?.original?.billType)?.label || row?.original?.billType,
    },
    {
      accessorKey: 'billDate',
      header: 'Bill Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.billDate),
    },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.dueDate),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      Cell: ({ row }) => fCurrency(row?.original?.totalAmount),
    },
    {
      accessorKey: 'paidAmount',
      header: 'Paid Amount',
      Cell: ({ row }) => fCurrency(row?.original?.paidAmount),
    },
    {
      accessorKey: 'outstandingAmount',
      header: 'Available Outstanding',
      Cell: ({ row }) => fCurrency(row?.original?.outstandingAmount),
    },
    { accessorKey: 'status', header: capitalize('status') },
  ];

  return (
    <Page title='Utility Bill Payment (Bills To Be Paid)'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Utility Bill Payment (Bills To Be Paid)'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Finance', href: PATH_DASHBOARD.finance.root }, { name: 'Utility Bill Payment (Bills To Be Paid)' }]}
          action={[]}
        />

        {accessVerify('FINANCE_UTILITY_BILL_TO_BE_VIEW') ? (
          <>
            <Card sx={{ m: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: '400' }}>
                    Total Amount To Be Paid
                  </Typography>
                  <Typography variant='h6' sx={{ color: 'red', fontWeight: '500' }}>
                    {fCurrency(totalAmountToBePaid)}
                  </Typography>
                </Box>
              </Box>
            </Card>
            <DataGridTable
              name='Utility Bills To Be Paid'
              data={utilityBillsToBePaid || []}
              column={columns}
              isLoading={false}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              enableRowActions={true}
              renderRowActionItems={(row) => [
                <Tooltip title='Download PDF' key='pdf'>
                  <IconButton color='error' onClick={() => handleDownload(row)}>
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>,
              ]}
            />
          </>
        ) : (
          <PermissionRequired />
        )}

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
